// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportViewerFilters.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observer } from 'mobx-react';
import React, { Component, PropTypes } from 'react';

import Panel from 'share/components/controls/Izenda.Panel';
import constants from 'logic/data/Izenda.ReportsConstants';
import FilterItem from 'modules/Reports/FilterProperty/Izenda.FilterItem';
import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import CrossFiltering from 'modules/CrossFiltering/Izenda.CrossFiltering';
import CrossFilterSetting from 'modules/CrossFiltering/Izenda.CrossFilterSetting';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
import ApplyFilterButton from '../Buttons/Izenda.ApplyFilterButton';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import Config from 'config';
import { isEmpty, flow, get, chunk, compact, head, some, getOr } from 'lodash/fp';
import { DEFAULT_DEBOUNCE_TIME } from 'logic/data/Izenda.UIConstants';
import { debounceLeading } from 'logic/models/Izenda.ReportUtils';
import Button from 'share/components/controls/Izenda.Button';

const integrationStyleAction = IntegrationStyleAction.checkDisplayByKey('hideFilter');

const FiltersHeader = connect()(
  ({
    localization,
    authorization,
    crossFilteringDetails,
    hadSetting,
    removeCrossFiltering,
    removeAllCrossFiltering,
    fieldStore,
    resetDefaultValue,
    enableDeleteAll,
    isReport,
    enableCrossFilteringOption,
    openPopup,
    collapseToggle,
    updateResultInfo
  }) => {
    const showCrossFilteringOption =
      isReport &&
      authorization.checkPrivileges(
        [
          AuthorizationConstants.PERMISSION_KEY.FullReportAndDashboardAccess,
          AuthorizationConstants.PERMISSION_KEY.ReportsFilterPropertiesCrossFiltering
        ],
        AuthorizationConstants.PERMISSION_OPERATOR.Or
      );

    /**
     * Open popup when click cross filter
     */
    const onCrossFilteringOptionClick = (): void => {
      openPopup(CrossFilterSetting, { store: fieldStore });
    };

    /**
     * Check show Cross Filtering Section
     */
    const validateShowCrossFilteringSection = (): boolean => {
      if (isReport) {
        return !isEmpty(crossFilteringDetails);
      } else {
        return !flow(
          head,
          get('pillDefinitions'),
          isEmpty
        )(crossFilteringDetails);
      }
    };
    const showCrossFiltering = validateShowCrossFilteringSection();

    /**
   * Reset All Filter in Report Viewer
   */
    const clearFilter = () =>{
     const{
      reportFilters:{configFieldList}
     }=fieldStore;

      configFieldList.map(item => {
        if (item.fieldDetails.value) {
          item.fieldDetails.updateByKey('value', null);
        }
      });
      
      if (fieldStore && fieldStore.filters)
        fieldStore.filters.map(index=> { index.setFilterValue(null); }); // Value sent to back-end for dashboards
    }

    return (
      <div className='u_table izenda-FilterHeader '>
        <div className='u_tableCell u_alignMiddle u_paddingRight' onClick={collapseToggle}>
          <i className='icon-toggle-thick' />
          <strong>{localization.getText('REPORT_LABEL_FILTERS')}</strong>
        </div>

        <div className='u_tableCell u_alignMiddle u_ullWidth izenda-FilterHeader__CrossFilteringContainer'>
          {showCrossFiltering && (
            <CrossFiltering
              crossFilteringDetail={crossFilteringDetails}
              showCrossFilteringOption={false}
              hadSetting={false}
              removeCrossFiltering={removeCrossFiltering}
              removeAllCrossFiltering={removeAllCrossFiltering}
              onCrossFilteringOptionClick={onCrossFilteringOptionClick}
              resetDefaultValue={resetDefaultValue}
              enableDeleteAll={enableDeleteAll}
              enableCrossFilteringOption={enableCrossFilteringOption}
            />
          )}
        </div>
        {updateResultInfo && updateResultInfo.accessPriority !== ACCESS_RIGHT.Locked && (
          <div className='izenda-FilterHeader__UpdateResult'>
            <ApplyFilterButton
              updateResults={updateResultInfo.updateResults}
              disabled={updateResultInfo.doesDisableUpdateResultBtn}
            />
            <Button
            onClick={clearFilter}
            className='btn-updateResult'>
            <span className='btn-updateResult-label'>{localization.getText('REPORTDESIGNER_LABEL_CLEAR_FILTER')}</span>
          </Button>
          </div>
        )}
      </div>
    );
  }
);

FiltersHeader.displayName = 'FiltersHeader';

@observer
class ReportViewerFilters extends Component {
  static propTypes = {
    configFields: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    fieldStore: PropTypes.object
  };
  
  static displayName = 'ReportViewerFilters';

  onScroll = debounceLeading(DEFAULT_DEBOUNCE_TIME, () => {
    const {
      fieldStore: { reportFilters }
    } = this.props;
    reportFilters.configField.fieldDetails = {};
  });

  render() {
    let {
      configFields,
      crossFilteringDetails,
      showCrossFilteringOption,
      hadSetting,
      removeCrossFiltering,
      removeAllCrossFiltering,
      resetDefaultValue,
      enableDeleteAll,
      isReport,
      enableCrossFilteringOption,
      accessPriority,
      isLazyLoading,
      afterToggled,
      afterMove,
      updateResultInfo
    } = this.props;
    let fieldStore = this.props.fieldStore || {};

    const header = {
      component: FiltersHeader,
      crossFilteringDetails: crossFilteringDetails,
      showCrossFilteringOption: showCrossFilteringOption,
      hadSetting: hadSetting,
      removeCrossFiltering: removeCrossFiltering,
      removeAllCrossFiltering: removeAllCrossFiltering,
      resetDefaultValue: resetDefaultValue,
      enableDeleteAll: enableDeleteAll,
      isReport: isReport,
      fieldStore: fieldStore,
      enableCrossFilteringOption: enableCrossFilteringOption,
      updateResultInfo
    };
     
    let filterItem = configFields.map((item, index) => {
      const updateCascadingValue = () => {
        return fieldStore.updateCascadingValue && fieldStore.updateCascadingValue.call(fieldStore, item);
      };
     
      return item.fieldDetails.visible &&
        item.fieldDetails.sourceFieldVisible &&
        item.fieldDetails.sourceFieldFilterable ? (
        <FilterItem
          key={index}
          index={index}
          itemData={item}
          updateCascadingValue={updateCascadingValue}
          displayName={get('fieldDetails.alias')}
          viewMode
          accessPriority={accessPriority}
          fieldStore={fieldStore}
          isLazyLoading={isLazyLoading}
        />
      ) : null;
    });

    let children = React.Children.count(this.props.children) === 0 ? null : this.props.children;

    let filterExpanded = Config.UIPreferences.ReportFilterSectionExpanded;
    if(!filterExpanded && Config.UIPreferences.RequiredFilterSectionExpanded){
      const filterFields = get('filters', fieldStore) || getOr([], 'reportFilters.filterFields', fieldStore);
      filterExpanded = some(filterField => filterField.required, filterFields);
    }

    return (
      <Panel
        className='filters-container view-mode-filter'
        header={header}
        collapsible
        expanedOption={filterExpanded}
        afterToggled={afterToggled}
        afterMove={afterMove}
        resizable
        defaultValue={constants.FITLERS_PANEL_UI.minHeight}
        minValue={constants.FITLERS_PANEL_UI.minHeight}
        maxValue={constants.FITLERS_PANEL_UI.maxHeight}
        toViewPortDimension
      >
        <GridLayout className='filter-body'>
          <GridLayout.Row className='u_fullHeight u_noMarginAll'>
            <ScrollBox onScroll={this.onScroll}>
              {filterItem}
              {children}
            </ScrollBox>
          </GridLayout.Row>
        </GridLayout>
      </Panel>
    );
  }
}

export default integrationStyleAction(ReportViewerFilters);



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewerFilters.js